import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Col, Container, Row } from 'react-bootstrap';
import { Modal } from '@storable/canaveral-components';
import AsyncFeedback from 'components/AsyncFeedback/AsyncFeedback';
import Authorization from 'containers/Authorization';
import { BUILDER } from 'shared/abilities';
import Button from '../../../../../../modules/react/components/stateless/Button';
import Form from '../../../../../../modules/react/components/stateful/Form';
import Input from '../../../../../../modules/react/components/stateful/Input';
import Field from '../../../../../../modules/react/components/stateful/Field';
import UnauthorizedBanner from '../UnauthorizedBanner';
import Certificate from './Certificate';
import classes from './CertSettings.scss';

const resolveFileContents = (file, encoding = null) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = function onReaderLoad() {
    resolve(reader.result);
  };

  reader.onerror = function onReaderError(err) {
    reject(err);
  };

  reader.readAsText(file, encoding);
});

export default class CertSettings extends React.Component {
  static propTypes = {
    certificates: PropTypes.array,
    hostnames: PropTypes.array,
    isSaving: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    updateCertificate: PropTypes.func.isRequired,
    errors: PropTypes.array,
  };

  static contextTypes = {
    setTab: PropTypes.func.isRequired,
  };

  static defaultProps = {
    certificates: [],
  };

  state = {
    deleteFingerprint: null,
  };

  UNSAFE_componentWillMount() {
    this.context.setTab('settings/ssl');
  }

  handleDelete = (fingerprint) => {
    this.setState({ deleteFingerprint: fingerprint });
  };

  handleDeleteCancel = () => {
    this.setState({ deleteFingerprint: null });
  };

  handleDeleteConfirm = () => {
    const { deleteFingerprint } = this.state;
    this.setState({ deleteFingerprint: null });
    this.props.onDelete(deleteFingerprint);
  };

  // So... this is a linting bug (https://github.com/yannickcr/eslint-plugin-react/issues/991)
  handleSubmit = async ({ certificate: certificates, key: keys }) => { // eslint-disable-line react/prop-types
    if (!certificates || !certificates.length) {
      // TODO: Handle this?
      return;
    }

    const { onSave, updateCertificate } = this.props;
    const [certificate] = certificates;
    const [key] = keys;
    const crtContents = await resolveFileContents(certificate);

    if (!keys && this.props.certificates.length) {
      await updateCertificate(this.props.certificates[0].fingerprint, crtContents);
    } else {
      const keyContents = await resolveFileContents(key);
      await onSave(crtContents, keyContents);
    }
  };

  renderDeleteModal() {
    if (!this.state.deleteFingerprint) {
      return false;
    }

    return (
      <Modal
        show
        onDismiss={this.handleDeleteCancel}
        aria-labelledby="cert-settings-modal"
      >
        <Modal.Header>
          <Modal.Title>Delete Certificate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You cannot undo this action. By deleting this certificate you are permanently removing it
            from your website. Are you sure you want to delete this certificate?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.handleDeleteCancel}>
            Cancel
          </Button>
          <Button onClick={this.handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderList() {
    const { certificates, hostnames } = this.props;
    if (!certificates) {
      return <div>Loading...</div>;
    } if (!certificates.length) {
      return <div className={classes.noCerts}>There are no certificates associated with this site.</div>;
    }

    return certificates.map((certificate, index) => (
      <Certificate
        key={index}
        {...certificate}
        siteHostnames={(hostnames || []).map(({ hostname }) => hostname)}
        onDelete={this.handleDelete}
      />));
  }

  renderForm() {
    const isSecure = typeof window !== 'undefined' &&
      typeof window.location !== 'undefined' &&
      window.location.protocol === 'https:';
    if (!__DEVELOPMENT__ && !isSecure) {
      return this.renderSecureWarning();
    }

    const { isSaving, errors, certificates } = this.props;

    return (
      <Form
        form="sslCertificates"
        onSubmit={this.handleSubmit}
        initialValues={{
          certificate: '',
          key: '',
        }}
      >
        <Container fluid>
          <Row>
            <Col xs={12}>
              <AsyncFeedback
                isSaving={isSaving}
                error={errors}
                success="Success"
              />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <Field
                component={Input}
                type="file"
                label="Certificate"
                name="certificate"
                required
              />
            </Col>
            <Col sm={6}>
              <Field
                component={Input}
                type="file"
                label="Key"
                name="key"
                required={!certificates || !certificates.length}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col sm={10}>
              <span className={classes.noCerts}>
                When a new certificate needs to be uploaded:
                Do NOT delete the old certificate file or the existing key file.
                Choose the new certificate, upload, and you are done!
              </span>
            </Col>
            <Col xs={2}>
              <Button
                variant="success"
                type="submit"
                disabled={isSaving}
                className={classes.uploadButton}
              >
                {isSaving ? 'Uploading...' : 'Upload'}
              </Button>
            </Col>
          </Row>
        </Container>
      </Form>
    );
  }

  renderSecureWarning() {
    return (
      <Alert variant="danger">
        <p>
          You are <strong>not</strong> connected to this page over SSL. Transmitting a certificate key over
          an unencrypted connection is <strong>extremely</strong> dangerous.
        </p>
        <p>
          For your security, uploading certificates over a non-secure connection has been disabled. Please
          connect over SSL if you want to add a new certificate.
        </p>
      </Alert>
    );
  }

  render() {
    const { certificates } = this.props;

    return (
      <Authorization action="manage" subject={BUILDER}>
        {authorized => (authorized ? (
          <React.Fragment>
            <h2>Certificates</h2>
            {this.renderList()}
            <h2>{certificates && certificates.length ? 'Add or Update ' : 'Add '}
              Certificate and/or Key
            </h2>
            {this.renderForm()}
            {this.renderDeleteModal()}
          </React.Fragment>
        ) : <UnauthorizedBanner />)}
      </Authorization>
    );
  }
}
